// M5.2 — quest templates + per-character quest log shapes.
//
// First-cut scope: kill quests only, one-shot (completable once per
// character), copper + XP rewards (no item rewards yet). Repeatable quests,
// delivery / explore objectives, and quest chains can layer on later via new
// fields on QuestObjective / QuestReward without breaking existing data.
//
// M7.2 made the runtime catalogue swappable via setQuestCatalogue, mirroring
// the items / mobs pattern.

import { z } from "zod";
import type { MobTemplateId } from "./mobs.js";

export type QuestObjectiveKind = "kill";

// Kill N of a given mob template. Progress is the count of credited kills.
// `mobTemplateId` is a plain string (not MobTemplateId literal union) so the
// admin can author quests that reference newly-added mobs — runtime cross-ref
// validation lives in the publish handler.
export interface QuestKillObjective {
  kind: "kill";
  mobTemplateId: string;
  count: number;
}

export type QuestObjective = QuestKillObjective;

export type QuestReward =
  | { kind: "copper"; amount: number }
  | { kind: "xp"; amount: number }
  // M7.2.x — item reward. `qty` is clamped server-side to maxStack on add.
  // Stackable items consolidate into existing stacks first then overflow
  // to fresh ones, matching adminGiveItem semantics. No affix roll —
  // quest rewards are predictable; use a rarity:"unique" template if you
  // want a fixed-affix legendary as a reward.
  | { kind: "item"; templateId: string; qty: number };

// Optional lines surfaced as NPC speech bubbles on the quest's life-cycle
// moments. `{name}` is interpolated server-side with the player's character
// name (same convention as `NpcTemplate.farewellText`).
export interface QuestSpeechLines {
  onAccept?: string;
  onTurnIn?: string;
  // onDecline left for a future "I refuse the quest" flow — no UI yet.
}

export interface QuestTemplate {
  id: string;
  // Short title shown in the quest-window row + on the NPC's available list.
  name: string;
  // Long-form pitch shown in the dialog body when the row is expanded.
  description: string;
  // Mutable (not readonly) for wire compatibility with the admin publish path.
  objectives: QuestObjective[];
  rewards: QuestReward[];
  speech?: QuestSpeechLines;
  // If true, completing + turning in this quest re-offers it on the NPC's
  // available list (progress resets on re-accept). Used for grindable
  // bounty-style quests. Defaults to false (one-shot per character).
  repeatable?: boolean;
}

// Capability block on NpcTemplate. The NPC offers the listed quests, in this
// order, when the client opens its dialog.
export interface NpcQuestData {
  questIds: string[];
}

// Per-character runtime state. `progress` is index-aligned with `objectives`
// on the template — element i is the credit count for objective i. `status`
// transitions: active → completable (all progress >= required) → completed
// (turned in). Completed entries stay in the log to prevent re-acceptance
// (one-shot rule).
export type QuestStatus = "active" | "completable" | "completed";

export interface QuestLogEntry {
  questId: string;
  status: QuestStatus;
  progress: number[];
  acceptedAt: number;
  completedAt?: number;
}

// =============================================================================
// Starter catalogue. Three kill quests anchored on existing mob templates
// (cave_rat, grey_wolf, goblin_warrior). Rewards are roughly the kill yield
// of one extra mob on top of what the kills themselves grant — a "thanks for
// playing through this content" stipend, not the main income source.
// Numbers are STARTING POINTS, tune in playtest.
// =============================================================================

const t = <T extends Record<string, QuestTemplate>>(o: T): T => o;

export const BUILTIN_QUEST_TEMPLATES = t({
  eldoria_cave_rat_hunt: {
    id: "eldoria_cave_rat_hunt",
    name: "Cave Rat Hunt",
    description:
      "The cellars are crawling with rats again. Thin them out — five tails should do it.",
    objectives: [{ kind: "kill", mobTemplateId: "cave_rat", count: 5 }],
    rewards: [
      { kind: "copper", amount: 30 },
      { kind: "xp", amount: 40 },
    ],
    speech: {
      onAccept: "Aye, hunt well, {name}.",
      onTurnIn: "Five tails — that's a fine job, {name}. Take this.",
    },
    repeatable: true,
  },
  eldoria_wolf_cull: {
    id: "eldoria_wolf_cull",
    name: "Wolf Cull",
    description:
      "The forest packs are thickening. Cull three Grey Wolves and the trail will be safer.",
    objectives: [{ kind: "kill", mobTemplateId: "grey_wolf", count: 3 }],
    rewards: [
      { kind: "copper", amount: 90 },
      { kind: "xp", amount: 150 },
    ],
    speech: {
      onAccept: "Watch the trees, {name}. They run in packs.",
      onTurnIn: "Three pelts — the road thanks you, {name}.",
    },
  },
  eldoria_goblin_scout: {
    id: "eldoria_goblin_scout",
    name: "Goblin Scout",
    description:
      "A goblin warrior has been seen pacing the eastern caves. Drop him and bring me his shield.",
    objectives: [{ kind: "kill", mobTemplateId: "goblin_warrior", count: 1 }],
    rewards: [
      { kind: "copper", amount: 250 },
      { kind: "xp", amount: 400 },
    ],
    speech: {
      onAccept: "Mind his blade, {name}. Goblins fight dirty.",
      onTurnIn: "One down, a hundred to go. Well struck, {name}.",
    },
  },
});

// Back-compat alias (tests iterate this).
export const QUEST_TEMPLATES = BUILTIN_QUEST_TEMPLATES;
export type QuestTemplateId = keyof typeof BUILTIN_QUEST_TEMPLATES;

// =============================================================================
// Runtime registry (M7.2)
// =============================================================================

const runtimeQuestRegistry = new Map<string, QuestTemplate>(
  Object.entries(BUILTIN_QUEST_TEMPLATES),
);

export function getQuestTemplate(id: string): QuestTemplate | undefined {
  return runtimeQuestRegistry.get(id);
}

export function getQuestCatalogue(): QuestTemplate[] {
  return Array.from(runtimeQuestRegistry.values());
}

export function setQuestCatalogue(quests: readonly QuestTemplate[]): void {
  runtimeQuestRegistry.clear();
  for (const q of quests) runtimeQuestRegistry.set(q.id, q);
}

export function _resetQuestCatalogueForTests(): void {
  runtimeQuestRegistry.clear();
  for (const [id, tpl] of Object.entries(BUILTIN_QUEST_TEMPLATES)) {
    runtimeQuestRegistry.set(id, tpl);
  }
}

// =============================================================================
// Zod schemas (M7.2)
// =============================================================================

const QUEST_ID_REGEX = /^[a-z][a-z0-9_]{2,60}$/;

const QuestKillObjectiveSchema = z
  .object({
    kind: z.literal("kill"),
    mobTemplateId: z.string().min(1),
    count: z.number().int().min(1).max(10_000),
  })
  .strict();

const QuestObjectiveSchema = QuestKillObjectiveSchema;

const QuestRewardSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("copper"),
      amount: z.number().int().min(0).max(10_000_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("xp"),
      amount: z.number().int().min(0).max(10_000_000),
    })
    .strict(),
  z
    .object({
      kind: z.literal("item"),
      templateId: z.string().min(1).max(60),
      qty: z.number().int().min(1).max(99),
    })
    .strict(),
]);

const QuestSpeechLinesSchema = z
  .object({
    onAccept: z.string().min(1).max(200).optional(),
    onTurnIn: z.string().min(1).max(200).optional(),
  })
  .strict();

export const QuestTemplateSchema = z
  .object({
    id: z.string().regex(QUEST_ID_REGEX),
    name: z.string().min(1).max(80),
    description: z.string().min(1).max(500),
    objectives: z.array(QuestObjectiveSchema).min(1).max(5),
    rewards: z.array(QuestRewardSchema).max(5),
    speech: QuestSpeechLinesSchema.optional(),
    repeatable: z.boolean().optional(),
  })
  .strict();

export const QuestCatalogueSchema = z.array(QuestTemplateSchema);

// True if every objective on the entry has met its required count. Returns
// false for "completed" entries (already turned in) — useful for both the
// active→completable promotion path (called after a credit increment) and
// the turn-in check (called by the router on questTurnIn).
export function isQuestCompletable(entry: QuestLogEntry, template: QuestTemplate): boolean {
  if (entry.status === "completed") return false;
  for (let i = 0; i < template.objectives.length; i++) {
    const required = template.objectives[i]!.count;
    const have = entry.progress[i] ?? 0;
    if (have < required) return false;
  }
  return true;
}
